import React, { useMemo } from 'react';
import { rgba } from 'polished';
import styled from '@emotion/styled';

import Button from '~/theseus/components/Button';
import Input from '~/theseus/components/Form/Input';
import ScreenReaderAlert from '~/theseus/components/Form/ScreenReaderAlert';

import Section from '../Section';
import Header from '../Header';
import Description from '../Description';
import InputField from '../Field';

import useCaptureFieldState from './useCaptureFieldState';
import makeFieldWithRegister from './Field';

const Container = styled.div`
  padding: 0 20px 20px 10px;
  margin: 20px;
`;

const Fieldset = styled.fieldset`
  border: none;

  margin: 20px 0;

  legend {
    display: block;
    margin-bottom: 5px;
    font-size: 12px;
    font-weight: 500;
    color: ${rgba('#000', 0.5)};
  }
`;

const AvailableQuestions = styled.div`
  display: ${props => (props.isShowing ? 'block' : 'none')};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: auto auto;
`;

function CaptureFields({ register }) {
  const { isViewingAll, toggleViewAll } = useCaptureFieldState();

  const Field = useMemo(() => makeFieldWithRegister(register), [register]);

  return (
    <Section>
      <Header>
        <h2>Lead Capture Fields</h2>
      </Header>
      <InputField htmlFor="lead-form-headline">
        <span className="label">Call to Action</span>
        <Input
          type="text"
          id="lead-form-headline"
          name="callToAction"
          ref={register}
          placeholder="Sign up by providing your info below. (Optional)"
          css={{ flexGrow: 2, marginRight: 20 }}
        />
      </InputField>
      <Description>What information would you like to ask for?</Description>
      <Container>
        <Fieldset>
          <legend>User information</legend>
          <ScreenReaderAlert>
            Email is selected for you by default
          </ScreenReaderAlert>
          <Grid>
            <Field name="fields.firstName" value="FIRST_NAME" label="First Name" />
            <Field name="fields.lastName" value="LAST_NAME" label="Last Name" />
            <Field name="fields.email" value="EMAIL" label="Email" disabled />
            <Field name="fields.phone" value="PHONE" label="Phone" />
          </Grid>
        </Fieldset>
        <Button type="button" variant="secondary" onClick={toggleViewAll}>
          {isViewingAll ? 'Fewer Options' : 'Show More Options'}
        </Button>
        <AvailableQuestions isShowing={isViewingAll}>
          <Fieldset>
            <legend>Contact Fields</legend>
            <Grid>
              <Field name="fields.state" value="STATE" label="State" />
              <Field name="fields.province" value="PROVINCE" label="Province" />
              <Field name="fields.country" value="COUNTRY" label="Country" />
            </Grid>
          </Fieldset>
          <Fieldset>
            <legend>Work Information</legend>
            <Grid>
              <Field name="fields.jobTitle" value="JOB_TITLE" label="Job Title" />
              <Field name="fields.workEmail" value="WORK_EMAIL" label="Work Email" />
              <Field name="fields.workPhone" value="WORK_PHONE_NUMBER" label="Work Phone Number" />
            </Grid>
          </Fieldset>
        </AvailableQuestions>
      </Container>
    </Section>
  );
}

export default React.memo(CaptureFields);
